import React from "react";
import { connect } from "react-redux";
import * as interviewActions from "../../../actions/interviewActions";
import { YesNoButtons } from "../../../components/FormComponents/YesNoButtons";
import { validationSchema } from "./validationSchema";
import * as formsService from "../../../services/formsService";
import { doesHaveCoverage, doesProvidedCoverageMatchesVerified } from "../../../services/coverageService";
import { HealthInsuranceForm } from "./HealthInsuranceForm";
import { ThirdPartyLiabilityFields } from "./ThirdPartyLiabilityFields";
import OnSubmitValidationError from "../../../components/FormComponents/OnSubmitValidationError";
import { cloneDeep, clone, merge, invert } from "lodash-es";
import { WizardButtonsField } from "../Components/WizardButtons";
import { CTA } from "@coverme/forms";
import { NoCoverage } from "@coverme/cards";
import "./InterviewCoverageContainer.scss";
import "./CoverageResultsStyle/CoverageResultsStyle.scss";
import { LoadingBar } from "../../../components/LoadingComponents/LoadingBar";
import InterviewCoverageForm from "../../../components/InterviewCoverageSummary/InterviewCoverageForm";
import CustomerEnabledFeature from "../../../components/CustomerEnabledFeature/CustomerEnabledFeature";
import { InterviewCoverageCard } from "../../../components/InterviewCoverageSummary/InterviewCoverageCard";
import { TooltipMessage, TooltipTrigger, Icon } from "@coverme/elements";
import { Tooltip } from "../../../components/Tooltips/Tooltip";
import { FieldArray, Formik, validateYupSchema, yupToFormErrors } from "formik";
import { HealthBenefitPlanCoverage } from "./HealthBenfitPlanCoverage";

const InterviewCoverage = ({
    onError,
    coverage,
    patientFullName,
    typesOfCoverage,
    triggerCoverageVerification,
    triggerCoverageDiscovery,
    interview,
    getIsFullyCovered,
    setPersonCoverage,
    updatePersonCoverage,
    deleteManuallyProvidedCoverage: deleteManuallyProvidedCoverage,
    getCoverage,
    updateCoverage,
    navigateToNextPage,
    navigateToPreviousPage,
    setContinueAfterCoverage,
    updateEncounterStatus,
    person,
    history,
    cancelCoverageDiscovery,
    customerId,
}) => {
    function getTooltipMessage(discoveryType) {
        switch (discoveryType) {
            case "Verification":
                return "Select to verify coverage information. Note: Member ID may be required for verification.";
            case "Discovery":
                return "Select to search all available coverage sources";
            case "DiscoveryDisabled":
                return "Coverage Search is not enabled for this account. Contact CoverMe for more details!";
            default:
                return null;
        }
    }

    function onVerifyCoverage(companyName, memberNumber) {
        return triggerCoverageVerification(companyName, memberNumber);
    }

    function onCoverageDiscovery() {
        return triggerCoverageDiscovery();
    }

    const onSubmit = data => {
        let interviewCoverage = cloneDeep(interview.coverage);
        merge(interviewCoverage, data);

        // recover hasCoverageBeenChecked from the state as it's not persisted on the component or form (better than useState?)
        interviewCoverage.hasCoverageBeenChecked = interview.coverage.hasCoverageBeenChecked;
        // default coverage clean-up
        if (interviewCoverage.hasCoverage) {
            if (interviewCoverage.primaryCoverage.companyName === "Other") {
                interviewCoverage.primaryCoverage.companyName = interviewCoverage.primaryCoverage.companyNameOther;
            } else {
                interviewCoverage.primaryCoverage.companyName = interviewCoverage.primaryCoverage.typeOfCoverage;
            }

            if (interviewCoverage.hasSupplementalCoverage) {
                if (interviewCoverage.supplementalCoverage.companyName === "Other") {
                    interviewCoverage.supplementalCoverage.companyName =
                        interviewCoverage.supplementalCoverage.companyNameOther;
                } else {
                    interviewCoverage.supplementalCoverage.companyName =
                        interviewCoverage.supplementalCoverage.typeOfCoverage;
                }
            }
        } else {
            interviewCoverage.primaryCoverage = undefined;
            interviewCoverage.supplementalCoverage = undefined;
        }
        if (
            (interviewCoverage.primaryCoverage?.memberNumber == undefined ||
                interviewCoverage.primaryCoverage?.memberNumber === "" ||
                interviewCoverage.primaryCoverage?.memberNumber == null) &&
            data.primaryCoverage.memberNumber != undefined &&
            data.primaryCoverage.memberNumber !== "" &&
            data.primaryCoverage.memberNumber != null
        ) {
            interviewCoverage.primaryCoverage.memberNumber = data.primaryCoverage.memberNumber;
        }
        return updateCoverage(interviewCoverage, interview.encounterId).then(() => {
            getCoverage(interview.encounterId);
            getIsFullyCovered(interview.encounterId).then(isFullyCovered => onPostSubmit(isFullyCovered));
        });
    };

    const onPostSubmit = isFullyCovered => {
        if (isFullyCovered) {
            // mark the encounter as complete
            updateEncounterStatus(interview.encounterId, "Complete");
            //return history.push(`/interview/${interview.encounterId}/details/dispatch`);
            navigateToNextPage(true, true);
        }

        if (doesHaveCoverage(interview)) {
            setContinueAfterCoverage(false);
            setTimeout(() => navigateToNextPage(false), 500);
        } else {
            navigateToNextPage(false, false);
        }
    };

    const autoProvidedPersonCoverages = interview.coverage.coverageSummary?.personCoverages?.filter(
        x => x.coverageSource.name != "Interview"
    );

    const onFormSubmit = async data => {
        let manuallyProvidedCoverages;
        if (data.provideManualCoverage || data.provideManualSupplementalCoverage) {
            manuallyProvidedCoverages = mapManualCoverageForm(data);
        }

        const autoProvidedPersonCoverages = interview.coverage.coverageSummary?.personCoverages?.filter(
            x => x.coverageSource.name != "Interview"
        );

        let allPersonCoverages = [];

        if (manuallyProvidedCoverages && autoProvidedPersonCoverages) {
            allPersonCoverages = manuallyProvidedCoverages.concat(autoProvidedPersonCoverages);
        } else if (manuallyProvidedCoverages && !autoProvidedPersonCoverages) {
            allPersonCoverages = manuallyProvidedCoverages;
        } else {
            allPersonCoverages = autoProvidedPersonCoverages;
        }

        if (allPersonCoverages?.length > 0) {
            allPersonCoverages.forEach(e => (e.encounterId = interview.encounterId));
            allPersonCoverages.forEach(e => (e.personId = person.data[interview.entry.patient.personId].globalId));
            allPersonCoverages.forEach(e => (e.externalPersonId = interview?.entry?.patient?.personId));
            await setPersonCoverage(allPersonCoverages);
        }
        return onSubmit(data);
    };

    const deleteProvidedPersonCoverage = criteria => {
        deleteManuallyProvidedCoverage(customerId, criteria);
        getIsFullyCovered(interview.encounterId);
    };

    const removeManualCoverageProvided = isPrimaryCoverage => {
        // delete if any
        if (
            interview.coverage.coverageSummary?.personCoverages.some(
                x => x.isPrimaryCoverage == isPrimaryCoverage && x.coverageSource.name === "Interview"
            ) &&
            isPrimaryCoverage
        ) {
            deleteProvidedPersonCoverage({
                healthInsuranceType: "Insurance",
                coverageSource: {
                    name: "Interview",
                },
                personId: interview.entry.patient.personId,
                encounterId: interview.globalId,
            });
        }
    };

    const addManualCoverageProvided = isPrimaryCoverage => {
        const newManualCoverageProvided = {
            healthInsuranceType: {
                name: "Insurance",
            },
            personId: interview.entry.patient.personId,
            isPrimaryCoverage: isPrimaryCoverage,
            encounterId: interview.encounterId,
            companyName: "",
            address: "",
            phoneNumber: "",
            plan: "",
            groupNumber: "",
            employer: "",
            memberId: "",
            copay: {
                erInPlanNetwork: "",
                office: "",
            },
            remainingDeductible: {
                inPlanNetwork: "",
            },
            remainingOutOfPocket: {
                inPlanNetwork: "",
            },
            coverageSource: {
                name: "Interview",
            },
        };

        updatePersonCoverage(newManualCoverageProvided);
    };

    const mapManualCoverageForm = data => {
        let personCoverages = [];
        const primaryCoverageProvidedIndex = manualPrimaryCoverageProvidedIndex();
        const supplementalCoverageProvidedIndex = manualSupplementalCoverageProvidedIndex();

        if (primaryCoverageProvidedIndex > -1) {
            const manualPrimaryCoverageProvided = data.coverageSummary.personCoverages[primaryCoverageProvidedIndex];
            personCoverages.push({
                healthInsuranceType: {
                    name: "Insurance",
                },
                personId: person.data[interview.entry.patient.personId].globalId,
                isPrimaryCoverage: true,
                companyName: manualPrimaryCoverageProvided.companyName,
                address: manualPrimaryCoverageProvided.address,
                phoneNumber: manualPrimaryCoverageProvided.phoneNumber,
                plan: manualPrimaryCoverageProvided.plan,
                groupNumber: manualPrimaryCoverageProvided.groupNumber,
                employer: manualPrimaryCoverageProvided.employer,
                memberId: manualPrimaryCoverageProvided.memberId,
                copay: manualPrimaryCoverageProvided.copay,
                remainingDeductible: manualPrimaryCoverageProvided.remainingDeductible,
                remainingOutOfPocket: manualPrimaryCoverageProvided.remainingOutOfPocket,
                coverageSource: {
                    name: "Interview",
                },
            });
        }

        if (supplementalCoverageProvidedIndex > -1) {
            const manualSupplementalCoverageProvided =
                data.coverageSummary.personCoverages[supplementalCoverageProvidedIndex];

            personCoverages.push({
                healthInsuranceType: {
                    name: "Insurance",
                },
                isPrimaryCoverage: false,
                personId: person.data[interview.entry.patient.personId].globalId,
                companyName: manualSupplementalCoverageProvided ? manualSupplementalCoverageProvided.companyName : "",
                address: manualSupplementalCoverageProvided ? manualSupplementalCoverageProvided.address : "",
                phoneNumber: manualSupplementalCoverageProvided ? manualSupplementalCoverageProvided.phoneNumber : "",
                plan: manualSupplementalCoverageProvided ? manualSupplementalCoverageProvided.plan : "",
                groupNumber: manualSupplementalCoverageProvided ? manualSupplementalCoverageProvided.groupNumber : "",
                employer: manualSupplementalCoverageProvided ? manualSupplementalCoverageProvided.employer : "",
                memberId: manualSupplementalCoverageProvided ? manualSupplementalCoverageProvided.memberId : "",
                coverageSource: {
                    name: "Interview",
                },
            });
        }

        return personCoverages;
    };

    const thePatient = patientFullName || "the patient";

    const isLoading = () => {
        return coverage.isCoverageSummaryLoading;
    };

    const manualPrimaryCoverageProvidedIndex = () =>
        interview?.coverage?.coverageSummary?.personCoverages.findIndex(
            coverage => coverage.isPrimaryCoverage && coverage.coverageSource.name === "Interview"
        );

    const manualSupplementalCoverageProvidedIndex = () =>
        interview?.coverage?.coverageSummary?.personCoverages.findIndex(
            coverage => !coverage.isPrimaryCoverage && coverage.coverageSource.name === "Interview"
        );

    const initialValues = {
        coverageSummary: coverage.coverageSummary,
        hasCoverage: coverage.hasCoverage,
        hasCoverageBeenChecked: coverage.hasCoverageBeenChecked,
        provideManualCoverage: coverage.provideManualCoverage,
        coverageConfirmed: coverage.coverageConfirmed,
        provideManualSupplementalCoverage: coverage.provideManualSupplementalCoverage,
        supplementalCoverageConfirmed: coverage.supplementalCoverageConfirmed,
        hasFullCoverage: coverage.isFullyCovered,
        hasSupplementalCoverage: coverage.hasSupplementalCoverage,
        isCoverageSummaryLoading: undefined,
        isFullyCovered: coverage.isFullyCovered,
        isSeekingMedicalServicesDueToAccidentOrCrime: coverage.isSeekingMedicalServicesDueToAccidentOrCrime,
        primaryCoverage: coverage.primaryCoverage,
        shouldContinue: undefined,
        supplementalCoverage: coverage.supplementalCoverage,
        thirdPartyLiability: {
            additionalNotes: coverage.thirdPartyLiability.additionalNotes,
            contactName: coverage.thirdPartyLiability.contactName,
            employerContactName: coverage.thirdPartyLiability.employerContactName,
            employerName: coverage.thirdPartyLiability.employerName,
            homeownerCompany: coverage.thirdPartyLiability.homeownerCompany,
            liabilityLimits: coverage.thirdPartyLiability.liabilityLimits,
            medPayCoverage: coverage.thirdPartyLiability.medPayCoverage,
            phoneNumber: coverage.thirdPartyLiability.phoneNumber,
            policyNumber: coverage.thirdPartyLiability.policyNumber,
            typeOfAccident: coverage.thirdPartyLiability.typeOfAccident,
            workCompCarrier: coverage.thirdPartyLiability.workCompCarrier,
        },
    };

    function validate(values) {
        const context = typesOfCoverage.map(x => ({
            name: x.name,
            requiresMemberId: x.requiresMemberId,
        }));

        return validateYupSchema(values, validationSchema, false, context)
            .then(() => {
                return {};
            })
            .catch(err => {
                throw yupToFormErrors(err);
            });
    }

    return (
        <div className="wizard-page">
            <h2 className="title">Coverage</h2>
            <Formik
                enableReinitialize={false}
                initialValues={initialValues}
                validate={validate}
                onSubmit={() => Promise.resolve()}
                render={props => {
                    const {
                        setFieldValue,
                        setFieldTouched,
                        resetForm,
                        handleSubmit,
                        errors,
                        touched,
                        values,
                        validateForm,
                    } = props;
                    const getValidationState = formsService.getValidationState.bind(this, errors, touched);
                    const isRequired = formsService.isRequired.bind(this, validationSchema, values);
                    const getErrors = formsService.getErrors.bind(this, errors);
                    return (
                        <>
                            {isLoading() && (
                                <h4>We are currently verifying for possible coverage payer sources. Hold tight.</h4>
                            )}
                            <LoadingBar isLoading={isLoading()} id="coverage-loading-bar">
                                <form className="wizard-form">
                                    <OnSubmitValidationError callback={onError} />
                                    <div className="form-section">
                                        <div className="form-control">
                                            <YesNoButtons
                                                name={"isSeekingMedicalServicesDueToAccidentOrCrime"}
                                                title={`Is ${thePatient} seeking medical services due to an accident or crime?`}
                                                getErrors={getErrors}
                                                isRequired={isRequired}
                                                getValidationState={getValidationState}
                                                {...props}
                                            />
                                        </div>
                                    </div>
                                    {values.isSeekingMedicalServicesDueToAccidentOrCrime && (
                                        <ThirdPartyLiabilityFields
                                            isRequired={isRequired}
                                            getValidationState={getValidationState}
                                            getErrors={getErrors}
                                            thePatient={thePatient}
                                            values={values}
                                            formikProps={props}
                                        />
                                    )}
                                    <div className="form-section">
                                        <div className="form-control">
                                            <YesNoButtons
                                                name={"hasCoverage"}
                                                title={`Does ${thePatient} have any type of healthcare coverage?`}
                                                getErrors={getErrors}
                                                getValidationState={getValidationState}
                                                isRequired={isRequired}
                                                {...props}
                                            />
                                        </div>
                                    </div>
                                    {values.hasCoverage && (
                                        <>
                                            <div className="form-section ie-heirarchy-container-2">
                                                <HealthInsuranceForm
                                                    getValidationState={getValidationState}
                                                    getErrors={getErrors}
                                                    setFieldValue={setFieldValue}
                                                    typesOfCoverage={typesOfCoverage.map(x => x.name)}
                                                    insuranceType="primaryCoverage"
                                                    insurance={values.primaryCoverage}
                                                    patientName={thePatient}
                                                    isRequired={isRequired}
                                                    {...props}
                                                />
                                                {console.log("Values of Primary Coverage: ", values.primaryCoverage)}
                                                {console.log("Has  Coverage: ", values.hasCoverage)}
                                            </div>
                                            {values.hasCoverage && values.typeOfCoverage !== "Other" && (
                                                <div className="form-section ie-heirarchy-container">
                                                    <div className="form-control">
                                                        <div className="submit-buttons on-search">
                                                            <Tooltip>
                                                                <TooltipTrigger>
                                                                    <CTA
                                                                        styleType="grey-dark"
                                                                        {...props}
                                                                        disabled={
                                                                            values.primaryCoverage.typeOfCoverage ==
                                                                            null
                                                                        }
                                                                        click={() => {
                                                                            validateForm().then(data => {
                                                                                if (
                                                                                    data?.primaryCoverage
                                                                                        ?.memberNumber === "Required"
                                                                                ) {
                                                                                    setFieldTouched(
                                                                                        "primaryCoverage.memberNumber"
                                                                                    );
                                                                                    return;
                                                                                }
                                                                                onVerifyCoverage(
                                                                                    values.primaryCoverage
                                                                                        .typeOfCoverage,
                                                                                    values.primaryCoverage.memberNumber
                                                                                );
                                                                            });
                                                                        }}
                                                                    >
                                                                        Verify Coverage &#x21BB;
                                                                    </CTA>
                                                                </TooltipTrigger>
                                                                <TooltipMessage>
                                                                    <div>{getTooltipMessage("Verification")}</div>
                                                                </TooltipMessage>
                                                            </Tooltip>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )}
                                    {coverage.hasCoverageBeenChecked && (
                                        <div className="form-section">
                                            <div className="form-control">
                                                <div className="interview-coverage-card">
                                                    {/* <HealthBenefitPlanCoverage
                                                        coverageValue={values.primaryCoverage.typeOfCoverage}
                                                        personCoverages={autoProvidedPersonCoverages}
                                                    /> */}
                                                    <InterviewCoverageCard
                                                        customerId={customerId}
                                                        encounterId={interview.encounterId}
                                                        coverage={coverage}
                                                        thePatient={thePatient}
                                                        title={`Coverage results`}
                                                        coverageValue={values.primaryCoverage.typeOfCoverage}
                                                        personCoverages={autoProvidedPersonCoverages}
                                                    />
                                                    {console.log("Coverage: ", coverage)}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    {((values.hasCoverage === false && coverage.hasCoverageBeenChecked === false) ||
                                        (coverage.coverageSummary?.personCoverages?.length === 0 &&
                                            coverage.hasCoverageBeenChecked === true)) && (
                                        <Tooltip>
                                            <TooltipTrigger>
                                                <CustomerEnabledFeature requiredFeature="Coverage Discovery">
                                                    <div className="form-section">
                                                        <div className="form-control">
                                                            <div className="submit-buttons on-search">
                                                                <Tooltip>
                                                                    <TooltipTrigger>
                                                                        <CTA
                                                                            styleType="grey-dark"
                                                                            click={onCoverageDiscovery}
                                                                            {...props}
                                                                        >
                                                                            Search for Coverage &#x21BB;
                                                                        </CTA>
                                                                    </TooltipTrigger>
                                                                    <TooltipMessage>
                                                                        <div>{getTooltipMessage("Discovery")}</div>
                                                                    </TooltipMessage>
                                                                </Tooltip>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </CustomerEnabledFeature>
                                            </TooltipTrigger>
                                            <TooltipMessage>
                                                <div id="discovery-disabled-tooltip">
                                                    {getTooltipMessage("DiscoveryDisabled")}
                                                </div>
                                            </TooltipMessage>
                                        </Tooltip>
                                    )}
                                    {coverage.coverageSummary?.error && (
                                        <div className="form-section">
                                            <div className="interview-coverage-summary">
                                                <h4 className="interview-coverage-summary__title">
                                                    {coverage.coverageSummary.error.title}
                                                </h4>
                                                <NoCoverage
                                                    title={coverage.coverageSummary.error.description}
                                                    subtitle={`Call insurer directly for more information`}
                                                />
                                            </div>
                                        </div>
                                    )}
                                    {values.hasCoverage &&
                                        coverage.hasCoverageBeenChecked &&
                                        coverage.coverageSummary?.personCoverages?.length === 0 && (
                                            <div className="interview-continue interview-coverage-inconsistency">
                                                <h3 className="interview-continue__title">
                                                    {coverage.primaryCoverage.typeOfCoverage === "Other" &&
                                                        "We are unable to verify information for the Insurance mentioned. You can use the form below to provide any additional details."}

                                                    {coverage.primaryCoverage.typeOfCoverage !== "Other" &&
                                                        "Coverage identified does not match. Please contact insurance company for further details."}
                                                </h3>
                                            </div>
                                        )}
                                    {(coverage.coverageSummary || values.provideManualCoverage) && (
                                        <div className="form-section">
                                            <div className="form-control">
                                                <div className="interview-continue interview-coverage-inconsistency">
                                                    <YesNoButtons
                                                        name={"provideManualCoverage"}
                                                        title={"Do you wish to provide further coverage information?"}
                                                        setFieldValue={setFieldValue}
                                                        isRequired={isRequired}
                                                        onFieldChange={isSelected => {
                                                            if (
                                                                !isSelected &&
                                                                coverage.provideManualCoverage !== undefined
                                                            ) {
                                                                removeManualCoverageProvided();
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    {values.provideManualCoverage && (
                                        <div className="form-section">
                                            <div className="form-control">
                                                <div className="interview-continue interview-coverage-inconsistency">
                                                    <YesNoButtons
                                                        name={"coverageConfirmed"}
                                                        title={`Does ${thePatient} have coverage?`}
                                                        setFieldValue={setFieldValue}
                                                        getErrors={getErrors}
                                                        isRequired={isRequired}
                                                        getValidationState={getValidationState}
                                                        {...props}
                                                        onFieldChange={isSelected => {
                                                            if (!isSelected) {
                                                                removeManualCoverageProvided(true);
                                                            } else {
                                                                addManualCoverageProvided(true);
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    {values.provideManualCoverage && values.coverageConfirmed && (
                                        // manualPrimaryCoverageProvidedIndex() > -1 &&
                                        <div className="interview-coverage-collected-form">
                                            <h3 className="interview-coverage-collected-form__title">
                                                Coverage Collected Information
                                            </h3>
                                            <InterviewCoverageForm
                                                baseKey={`coverageSummary.personCoverages[${manualPrimaryCoverageProvidedIndex()}]`}
                                                {...props}
                                            />
                                        </div>
                                    )}
                                    {values.coverageConfirmed && values.provideManualCoverage && (
                                        <div className="interview-continue interview-coverage-inconsistency">
                                            <h3 className="interview-continue__title">
                                                Supplemental Coverage Information
                                            </h3>
                                            <div className="interview-continue interview-coverage-inconsistency">
                                                <YesNoButtons
                                                    name={"provideManualSupplementalCoverage"}
                                                    title={"Do you wish to provide supplemental coverage information?"}
                                                    setFieldValue={setFieldValue}
                                                    isRequired={isRequired}
                                                    onFieldChange={isSelected => {
                                                        if (!isSelected) {
                                                            removeManualCoverageProvided(false);
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    )}
                                    {values.provideManualSupplementalCoverage && values.provideManualCoverage && (
                                        <div className="form-section">
                                            <div className="form-control">
                                                <div className="interview-continue interview-coverage-inconsistency">
                                                    <YesNoButtons
                                                        name={"supplementalCoverageConfirmed"}
                                                        title={`Does ${thePatient} have supplemental coverage?`}
                                                        setFieldValue={setFieldValue}
                                                        isRequired={isRequired}
                                                        onFieldChange={isSelected => {
                                                            if (!isSelected) {
                                                                removeManualCoverageProvided(false);
                                                            } else {
                                                                addManualCoverageProvided(false);
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    {values.provideManualSupplementalCoverage &&
                                        values.supplementalCoverageConfirmed &&
                                        values.provideManualCoverage && (
                                            <div className="interview-coverage-collected-form">
                                                <h3 className="interview-coverage-collected-form__title">
                                                    Supplemental Coverage Collected Information
                                                </h3>
                                                <InterviewCoverageForm
                                                    baseKey={`coverageSummary.personCoverages[${manualSupplementalCoverageProvidedIndex()}]`}
                                                    isPrimaryCoverage={false}
                                                    {...props}
                                                />
                                            </div>
                                        )}

                                    {
                                        <WizardButtonsField
                                            isFirstPage={false}
                                            navigateToNextPage={onFormSubmit}
                                            navigateToPreviousPage={navigateToPreviousPage}
                                        />
                                    }
                                    <div className="required_field_indicator_explained">Indicates required field</div>
                                </form>
                            </LoadingBar>
                            {isLoading() && (
                                <div className="form-section ie-heirarchy-container" id="cancel-coverage-check">
                                    <div className="form-control">
                                        <div className="submit-buttons on-search">
                                            <CTA
                                                size={"large"}
                                                ctaType={"button"}
                                                styleType={"grey-dark-alt"}
                                                click={cancelCoverageDiscovery}
                                            >
                                                Cancel
                                            </CTA>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    );
                }}
            />
        </div>
    );
};

export const InterviewCoverageContainer = connect(
    ({ interview, interviewLookups: lookups, person, customer }) => ({
        coverage: interview.coverage,
        patientFullName: interview.entry.patient.fullName,
        typesOfCoverage: lookups.data.typeOfCoverage,
        person: person,
        interview: interview,
        customerId: customer?.data?.customer?.GlobalId,
    }),
    dispatch => ({
        onError: () => dispatch(interviewActions.setInterviewError("coverage")),
        triggerCoverageVerification: (companyName, memberNumber) =>
            dispatch(interviewActions.triggerCoverageVerification(companyName, memberNumber)),
        triggerCoverageDiscovery: () => dispatch(interviewActions.triggerCoverageDiscovery()),
        getIsFullyCovered: encounterId => dispatch(interviewActions.getIsFullyCovered(encounterId)),
        setPersonCoverage: interview => dispatch(interviewActions.setPersonCoverage(interview)),
        updatePersonCoverage: interview => dispatch(interviewActions.updatePersonCoverage(interview)),
        deleteManuallyProvidedCoverage: (customerId, personId) =>
            dispatch(interviewActions.deleteManuallyProvidedCoverage(customerId, personId)),
        getCoverage: encounterId => dispatch(interviewActions.getCoverage(encounterId)),
        updateCoverage: (data, encounterId) => dispatch(interviewActions.setCoverage(data, encounterId)),
        setContinueAfterCoverage: value => dispatch(interviewActions.setContinueAfterCoverage(value)),
        updateEncounterStatus: (encounterId, status) =>
            dispatch(interviewActions.updateEncounterStatus(encounterId, status)),
        cancelCoverageDiscovery: () => dispatch(interviewActions.cancelCoverageDiscovery()),
    })
)(InterviewCoverage);
